/* @jsxImportSource react */

import type { DimensionFragmentFragmentDoc } from '@packages/gql/generated/shopping/DimensionFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';

import type { VariationSelectionData } from '../types';
import type { VariationSelectionProps } from '../VariationSelection';

import { ColorTilesDimension } from '../ColorTilesDimension/ColorTilesDimension';
import { TilesDimension } from '../TilesDimension/TilesDimension';
import { SelectDimension } from '../SelectDimension/SelectDimension.client';

export type DimensionWrapperProps = Omit<VariationSelectionProps, 'data'> & {
  dimensionData: VariationSelectionData[number];
};

/**
 * DimensionWrapper component decides which component-type to be rendered:
 * There are multiple ways, a product and its variations can be selected (e.g. Select, Tiles, ...).
 * Depending on configuration and backend-data, this component chooses a suiting
 * display-type.
 * */
export const DimensionWrapper = ({
  dimensionData,
  sampleOrderLink,
  onSelect,
}: DimensionWrapperProps) => {
  const unmaskedData = unmask<typeof DimensionFragmentFragmentDoc>(dimensionData);

  const { axisType, displayName, type, values } = unmaskedData;

  let label = displayName;
  let content = <SelectDimension data={unmaskedData} onSelect={onSelect} />;

  if (type === 'TILES' || type === 'PRICE_TILES') {
    if (axisType === 'COLOR') {
      const selectedDimension = values.find((value) => value.isSelected);
      if (selectedDimension) {
        label = `${label}: ${selectedDimension.displayText}`;
      }
      content = <ColorTilesDimension data={values} onSelect={onSelect} />;
    } else {
      content = <TilesDimension data={values} onSelect={onSelect} />;
    }
  }

  return (
    <article className="space-y-1">
      <header className="text-size-body-sm">{label}</header>
      <div>{content}</div>
      {sampleOrderLink !== null && axisType === 'COLOR' && sampleOrderLink}
    </article>
  );
};
